<template>
  <div>
    <v-menu
      open-on-hover
      bottom
      offset-y
      :close-on-content-click="false"
      :close-on-click="false"
      content-class="profile-dropdown"
      min-width="220"
      :open-delay="0"
      :close-delay="0"
    >
      <template v-slot:activator="{ on, attrs }">
        <button class="profile-btn" v-bind="attrs" v-on="on">
          <div class="profile-avatar">
            <img v-if="auth.avatarUrl" :src="auth.avatarUrl" alt="avatar" class="profile-avatar__img" referrerpolicy="no-referrer" />
            <template v-else>{{ userInitials }}</template>
          </div>
          <span class="profile-name">{{ auth.name }}</span>
          <v-icon size="14" color="var(--app-text-faint)">mdi-chevron-down</v-icon>
        </button>
      </template>

      <v-card flat class="profile-card">
        <!-- User Info Header -->
        <div class="profile-card__header">
          <div class="profile-card__avatar">
            <img v-if="auth.avatarUrl" :src="auth.avatarUrl" alt="avatar" class="profile-card__avatar-img" referrerpolicy="no-referrer" />
            <template v-else>{{ userInitials }}</template>
          </div>
          <div>
            <div class="profile-card__name">{{ auth.name }}</div>
            <div class="profile-card__email">{{ auth.email || '' }}</div>
          </div>
        </div>

        <v-divider></v-divider>

        <!-- Roles -->
        <div class="profile-card__section">
          <div class="profile-card__section-title">
            <v-icon size="14" color="var(--app-text-faint)" class="mr-1">mdi-shield-account-outline</v-icon>
            My Roles
          </div>
          <div class="profile-card__roles">
            <span
              v-for="(role, index) in auth.roles"
              :key="index"
              class="profile-card__role-chip"
            >
              {{ role.name }}
            </span>
          </div>
        </div>

        <v-divider></v-divider>

        <!-- My Profile -->
        <div class="profile-card__footer">
          <button class="profile-card__action" @click="goToProfile()">
            <v-icon size="16" color="var(--app-text-muted)" class="mr-2">mdi-account-cog-outline</v-icon>
            My Profile
          </button>
        </div>

        <v-divider></v-divider>

        <!-- Logout -->
        <div class="profile-card__footer">
          <button class="profile-card__logout" @click="toggleLogoutDialogChild(true)">
            <v-icon size="16" color="var(--app-danger)" class="mr-2">mdi-logout</v-icon>
            Logout
          </button>
        </div>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "AppHeaderProfile",
  emits: ["toggleLogoutDialogChild"],
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
    userInitials() {
      if (!this.auth.name) return '?';
      return this.auth.name
        .split(' ')
        .map(n => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    },
  },
  methods: {
    toggleLogoutDialogChild(bool) {
      this.$emit("toggleLogoutDialogChild", bool);
    },
    goToProfile() {
      if (this.$route.name !== "PageConsoleProfile") {
        this.$router.push({ name: "PageConsoleProfile" });
      }
    },
  },
};
</script>

<style>
/* The `.dark-mode` companion block that used to open this file is gone: every
   rule below resolves its colours through var(--app-*), which already flip with
   `.dark-mode` on <html>. A second, dark-only copy of these rules would restate
   them at higher specificity and quietly repaint the frosted card opaque.

   NOT scoped, on purpose. Vuetify detaches v-menu content out of the owning
   component and the detached nodes don't carry this file's data-v-* attribute,
   so a scoped block could not reach the open profile card at all. */

/* ===== Profile Button ===== */
/* Pill + neutral veil, matching the nav buttons in LayoutConsoleV2 — this
   button sits inside the same glass bar and has to read as the same family of
   control, not as a second kind of chrome. */
.profile-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px 4px 4px;
  border: none;
  background: transparent;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  font-family: 'Lato', sans-serif;
}

.profile-btn:hover {
  background-color: var(--app-hover);
}

.profile-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--app-ring);
}

.profile-avatar {
  width: 30px;
  height: 30px;
  border-radius: 10px;
  background: var(--app-brand-gradient);
  color: var(--app-brand-gradient-fg);
  font-size: 0.72rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.5px;
  overflow: hidden;
  flex: 0 0 auto;
}

.profile-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--app-text);
  white-space: nowrap;
}

/* ===== Profile Dropdown Card ===== */
/* Same material as the navbar and its menus: opaque baseline, frosted where
   backdrop-filter exists. See the geometry/material note in LayoutConsoleV2.
   The `html …` prefix and the doubled class are load-bearing for the same
   reason spelled out there: a dozen page files inject UNSCOPED
   `.dark-mode .v-menu__content { background: var(--app-card) !important }`
   rules that repaint every menu in the app, this card included. */
html .profile-dropdown.v-menu__content {
  border-radius: 16px !important;
  background: var(--app-navbar) !important;
  border: 1px solid var(--app-border);
  box-shadow:
    0 12px 34px var(--app-navbar-shadow),
    inset 0 1px 0 var(--app-inner-highlight) !important;
  margin-top: 6px !important;
  max-width: 280px !important;
  /* Vuetify clips `.v-menu__content` with `overflow-y: auto` + `contain: content`;
     without lifting both, the hover bridge below is clipped away and inert.
     See the longer note in LayoutConsoleV2.vue. */
  overflow: visible !important;
  contain: none !important;
}

/* HOVER BRIDGE — the same one the navbar dropdowns carry; see the long note in
   LayoutConsoleV2.vue. The 6px above detaches this card from the floating bar,
   and an `open-on-hover` menu treats that strip as "outside", so travelling
   from the avatar button down into the card used to close it mid-move. This
   fills the strip as an invisible part of the card. Sized to the offset so it
   stops flush with the button rather than swallowing clicks on it.
   That is also why this panel is no longer `overflow: hidden` — .profile-card
   below already clips its own children to the same 16px radius. */
html .profile-dropdown.v-menu__content::before {
  content: "";
  position: absolute;
  top: -7px; /* 6px margin-top + the 1px border above the padding box */
  left: -1px;
  right: -1px;
  height: 7px;
}

@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  html .profile-dropdown.v-menu__content {
    background: var(--app-menu-glass) !important;
    -webkit-backdrop-filter: blur(18px) saturate(180%);
    backdrop-filter: blur(18px) saturate(180%);
  }
}

/* The card is a v-sheet and paints its own theme background, which would sit as
   an opaque slab on top of the frosted panel above. */
html .profile-dropdown.v-menu__content .profile-card {
  border-radius: 16px !important;
  background: transparent !important;
  overflow: hidden;
}

.profile-card .v-divider {
  border-color: var(--app-border) !important;
}

.profile-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
}

.profile-card__avatar {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: var(--app-brand-gradient);
  color: var(--app-brand-gradient-fg);
  font-size: 0.82rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.profile-card__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-card__name {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--app-text);
}

.profile-card__email {
  font-size: 0.72rem;
  color: var(--app-text-faint);
  margin-top: 1px;
  word-break: break-all;
}

/* Roles Section */
.profile-card__section {
  padding: 10px 16px;
}

.profile-card__section-title {
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--app-text-faint);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.profile-card__roles {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.profile-card__role-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 6px;
  background: var(--app-accent-soft);
  color: var(--app-accent-strong);
  font-size: 0.68rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
}

/* Footer actions */
.profile-card__footer {
  padding: 6px 12px;
}

.profile-card__action,
.profile-card__logout {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  font-family: 'Lato', sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.profile-card__action {
  color: var(--app-text);
}

.profile-card__action:hover {
  background-color: var(--app-accent-soft);
  color: var(--app-accent);
}

.profile-card__logout {
  color: var(--app-danger);
}

.profile-card__logout:hover {
  background-color: var(--app-danger-bg);
}
</style>
